/**
 * GFG-364 v2 — the pure rules behind Add Recipients (spec Part B): the single-add wizard, the bulk
 * checkout, the send-time step and the payment review. Split out of the components so each rule is
 * testable (`addRecipients-helper.test.ts`) without mounting a dialog.
 *
 * The free/paid split, the deposit mode and every wallet and credit figure are server decisions
 * (contract D1, contract-c2 C3-C6): the quote arrives as decimal strings and is only formatted
 * here, with `formatUsd`. The one split computed client-side (`splitAdditions`) feeds the details
 * step's cost line and the first plan, before any quote. Credits the wallet lacks are bought in
 * the Buy Credits modal; the write sends the id of the purchase made there. The payment step's lines
 * and recap live with the payment rules (`cardPurchase-helper.ts`) and are re-exported here.
 */

import { COPY } from "@/constants/campaignContacts";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import type { Recipient } from "@/interfaces/CampaignBuilder";
import type {
  AddCampaignRecipientsBody,
  AddedRowsState,
  AdditionSendAtBody,
  AdditionsExpected,
  CampaignContactWire,
  RecipientAdditionRowBody,
  RecipientAdditionsQuote,
} from "@/interfaces/campaignContacts";
import { matchCountryCode, resolveCountryCodeOrUs } from "@/lib/country";
import type { CampaignStatus } from "@/types/types";
import { formatDateLong } from "@/utils/date-format";
import { formatUsd } from "@/utils/number-format";
import { resolveScheduleTimeZone, zonedWallTimeToIso } from "@/utils/timezone-helper";
import {
  contactEmailKey,
  formatSendInstant,
  isCalendarDateInput,
  isFutureSendDateInput,
  isValidContactEmail,
  minSendDateInput,
  zonedDateInput,
} from "./campaignContacts-helper";
import { canContinuePayment } from "./cardPurchase-helper";
import type {
  BulkImportAction,
  BulkImportOutcome,
  BulkImportReview,
  BulkImportState,
  CampaignContactsLock,
  RecipientFormErrors,
  RecipientFormValue,
  SendTimeCampaign,
  SendTimeChoice,
  SendTimeMode,
  SendTimeOption,
  WizardAction,
  WizardEntry,
  WizardPlan,
  WizardPurchase,
  WizardState,
  WizardStep,
} from "./types";

/** The default "later" time of day — the prototype's `cxSendWhenDefault` picks 9 AM. */
const DEFAULT_SEND_TIME = "09:00";
/** A confirm refused over money (quote changed, credits moved, credit limit): back to payment. */
export const MONEY_REFUSAL_STATUS = 422;
/** A confirm refused for an email already on the campaign: a single add fixes it on the form. */
const DUPLICATE_STATUS = 409;
/** Canonical step order, used to clamp a step the new plan no longer has. */
const STEP_ORDER: readonly WizardStep[] = ["details", "payment", "sendTime"];
const ADDABLE_STATUSES: readonly CampaignStatus[] = [
  CAMPAIGN_STATUSES.DRAFT,
  CAMPAIGN_STATUSES.SCHEDULED,
  CAMPAIGN_STATUSES.ACTIVE,
];

// ---- free / paid split and the Add recipients menu ----------------------------------------------

/** The first `spacesFree` recipients fill paid-for spaces; the rest are paid adds (R11). */
export function splitAdditions(n: number, spacesFree: number): { free: number; paid: number } {
  const free = Math.min(n, Math.max(0, spacesFree));
  return { free, paid: n - free };
}

/** The footer's ADD RECIPIENTS menu: editors only, on a draft/scheduled/active send campaign. */
export function addRecipientsMenuVisible(i: {
  canEdit: boolean;
  canAddRecipients: boolean;
  status: CampaignStatus;
  manual: boolean;
  lock: CampaignContactsLock | null;
}): boolean {
  return (
    i.canEdit &&
    i.canAddRecipients &&
    !i.manual &&
    i.lock === null &&
    ADDABLE_STATUSES.includes(i.status)
  );
}

/** The sub-line under "Add one recipient": no charge on a draft, else free spaces then price. */
export function addOneSubtitle(i: {
  draft: boolean;
  spacesFree: number;
  pricePerGift: number;
  replacementOnly?: boolean;
}): string {
  const sub = COPY.addMenu.oneSub;
  if (i.draft) return sub.draft;
  if (i.replacementOnly) return sub.freeOnly(i.spacesFree);
  const price = formatUsd(i.pricePerGift);
  return i.spacesFree > 0 ? sub.withFree(i.spacesFree, price) : sub.perRecipient(price);
}

/** The details step's cost line (B3); none on a draft, which has no budget yet. */
export function detailsCostLine(i: {
  draft: boolean;
  n: number;
  spacesFree: number;
  pricePerGift: number;
}): { kind: "free" | "paid"; text: string } | null {
  if (i.draft || i.n <= 0) return null;
  const { paid } = splitAdditions(i.n, i.spacesFree);
  if (paid === 0) return { kind: "free", text: COPY.addWizard.costFree(i.n, i.spacesFree) };
  return {
    kind: "paid",
    text: COPY.addWizard.costPaid(paid, formatUsd(paid * i.pricePerGift)),
  };
}

// ---- send time (B7, R13, R24) -------------------------------------------------------------------

/** A scheduled or active campaign that is not Manual Distribution: its additions are emailed. */
function sendsGiftEmails(c: SendTimeCampaign): boolean {
  const live = c.status === CAMPAIGN_STATUSES.SCHEDULED || c.status === CAMPAIGN_STATUSES.ACTIVE;
  return live && !c.manual;
}

/** The zone a campaign's times are read in: its Schedule pick, Eastern when it has none. */
function campaignZone(c: SendTimeCampaign): string {
  return resolveScheduleTimeZone(c.scheduledTz);
}

/** The earliest instant a "later" send may follow: now, or the scheduled launch when later. */
function sendTimeFloor(c: SendTimeCampaign, now: Date): number {
  const launch =
    c.status === CAMPAIGN_STATUSES.SCHEDULED && c.scheduledDate
      ? Date.parse(c.scheduledDate)
      : Number.NaN;
  return Number.isNaN(launch) ? now.getTime() : Math.max(now.getTime(), launch);
}

/**
 * The send-time choices: Scheduled → with the campaign / later; Active → now / later. Automations
 * (the date is in the details form), drafts, closed and Manual Distribution campaigns have none.
 * The launch and the close are read in the campaign's zone.
 */
export function sendTimeOptions(c: SendTimeCampaign): SendTimeOption[] {
  if (!sendsGiftEmails(c) || c.automation) return [];
  const o = COPY.addWizard.sendOptions;
  const zone = campaignZone(c);
  const first: SendTimeOption =
    c.status === CAMPAIGN_STATUSES.SCHEDULED
      ? {
          mode: "with_campaign",
          label: o.withCampaign,
          sub: formatSendInstant(c.scheduledDate, zone),
        }
      : { mode: "now", label: o.now, sub: o.nowSub };
  const closeDay = zonedDateInput(c.expireDate, zone);
  const laterSub = closeDay ? o.laterSubExpiry(formatDateLong(closeDay)) : o.laterSub;
  return [first, { mode: "later", label: o.later, sub: laterSub }];
}

/**
 * Whether the wizard shows a send-time step: whenever there are options, and on a bulk automation
 * with an undated row (one batch date then covers them — the prototype's `amHasSendStep`).
 */
export function hasSendTimeStep(
  c: SendTimeCampaign,
  entry: WizardEntry,
  rows: readonly { sendDate?: string }[],
): boolean {
  if (sendTimeOptions(c).length > 0) return true;
  return entry === "bulk" && c.automation && sendsGiftEmails(c) && rows.some((r) => !r.sendDate);
}

/**
 * With the campaign (Scheduled) or now (Active); the "later" fields default to 9 AM on the day
 * after now (or after the launch), a calendar day in the campaign's zone.
 */
export function defaultSendTime(c: SendTimeCampaign, now: Date): SendTimeChoice {
  const mode: SendTimeMode = c.status === CAMPAIGN_STATUSES.SCHEDULED ? "with_campaign" : "now";
  const date = minSendDateInput(campaignZone(c), new Date(sendTimeFloor(c, now)));
  return { mode, date, time: DEFAULT_SEND_TIME };
}

/** Whether `atIso` falls strictly before the close instant; no close date means always. */
export function isSendAtBeforeExpiry(atIso: string, expireDate: string | null): boolean {
  if (expireDate === null) return true;
  return Date.parse(atIso) < Date.parse(expireDate);
}

/**
 * Why the send-time choice can't be confirmed, or null — mirrors the API's `sendAtRefusal`. The
 * "later" wall clock is read in the campaign's zone, and the launch and close are named in it.
 */
export function sendTimeError(
  c: SendTimeCampaign,
  choice: SendTimeChoice,
  now: Date,
): string | null {
  const errors = COPY.addWizard.sendErrors;
  const modes = sendTimeOptions(c).map((o) => o.mode);
  if (modes.length > 0 && !modes.includes(choice.mode)) return errors.required;
  if (choice.mode !== "later") return null;
  const zone = campaignZone(c);
  const at = zonedWallTimeToIso(choice.date, choice.time, zone);
  if (at === null) return errors.required;
  const atMs = Date.parse(at);
  if (atMs <= now.getTime()) return errors.past;
  if (atMs <= sendTimeFloor(c, now)) {
    return errors.pastScheduled(formatSendInstant(c.scheduledDate, zone));
  }
  if (!isSendAtBeforeExpiry(at, c.expireDate)) {
    return errors.afterExpiry(formatSendInstant(c.expireDate, zone));
  }
  return null;
}

/**
 * The wire `sendAt`. "Later" sends the wall clock read in `timeZone` (the campaign's) as an ISO
 * instant; a malformed one goes as an empty instant, which the server refuses with its own message
 * (callers check `sendTimeError`).
 */
export function toSendAt(choice: SendTimeChoice, timeZone: string): AdditionSendAtBody {
  if (choice.mode === "now") return { mode: "now" };
  if (choice.mode === "with_campaign") return { mode: "with_campaign" };
  return { mode: "later", at: zonedWallTimeToIso(choice.date, choice.time, timeZone) ?? "" };
}

// ---- payment review (B4, B5, R14, R15): lines, recap, deposit choice: `cardPurchase-helper.ts` --

export {
  canContinuePayment,
  creditLimitNotice,
  paymentLines,
  paymentRecap,
} from "./cardPurchase-helper";

// ---- wizard (R16) -------------------------------------------------------------------------------

/** Single: details, then payment when paid, then send time. Bulk: payment, then send time. */
export function wizardSteps(plan: WizardPlan): WizardStep[] {
  if (plan.draft) return ["details"];
  const details: WizardStep[] = plan.entry === "single" ? ["details"] : [];
  const payment: WizardStep[] = plan.entry === "bulk" || plan.paid ? ["payment"] : [];
  const sendTime: WizardStep[] = plan.sendStep ? ["sendTime"] : [];
  return [...details, ...payment, ...sendTime];
}

export function initialWizardState(
  plan: WizardPlan,
  sendTime: SendTimeChoice,
  batchSendDate: string,
): WizardState {
  const [first = "details"] = wizardSteps(plan);
  return {
    step: first,
    plan,
    sendTime,
    batchSendDate,
    notice: null,
    busy: false,
    purchase: null,
    depositPreference: "card",
  };
}

/** The wizard's steps for an addition: a draft is never paid; the quote decides once it is in. */
export function wizardPlanOf(i: {
  entry: WizardEntry;
  draft: boolean;
  n: number;
  spacesFree: number;
  quote: RecipientAdditionsQuote | undefined;
  sendStep: boolean;
}): WizardPlan {
  const paid = i.quote ? i.quote.paidCount > 0 : splitAdditions(i.n, i.spacesFree).paid > 0;
  const live = !i.draft;
  return { entry: i.entry, draft: i.draft, paid: live && paid, sendStep: live && i.sendStep };
}

export function samePlan(a: WizardPlan, b: WizardPlan): boolean {
  return (
    a.entry === b.entry && a.draft === b.draft && a.paid === b.paid && a.sendStep === b.sendStep
  );
}

/** Whether the wizard is on its plan's last step, the one whose primary button writes. */
export function isLastStep(state: WizardState): boolean {
  const steps = wizardSteps(state.plan);
  return state.step === steps[steps.length - 1];
}

/** The step `offset` away from the current one, held inside the plan's steps. */
function stepBy(state: WizardState, offset: number): WizardStep {
  const steps = wizardSteps(state.plan);
  const index = Math.min(Math.max(steps.indexOf(state.step) + offset, 0), steps.length - 1);
  return steps[index] ?? state.step;
}

/** `step` when the plan still has it, else the nearest earlier step it has, else its first. */
function clampStep(step: WizardStep, plan: WizardPlan): WizardStep {
  const steps = wizardSteps(plan);
  if (steps.includes(step)) return step;
  const rank = STEP_ORDER.indexOf(step);
  const earlier = steps.filter((s) => STEP_ORDER.indexOf(s) < rank);
  return earlier[earlier.length - 1] ?? steps[0] ?? step;
}

/**
 * A refused confirm. A money refusal (422) goes back to the figures, keeping any purchase made in
 * the Buy Credits modal (the credits stay in the wallet, B9); a
 * duplicate email (409) on a single add goes back to the form, where the email is fixed. Anything
 * else keeps the step.
 */
function failedState(state: WizardState, status: number, message: string): WizardState {
  const base: WizardState = { ...state, notice: message, busy: false };
  const steps = wizardSteps(state.plan);
  if (status === MONEY_REFUSAL_STATUS) {
    const step = steps.includes("payment") ? "payment" : state.step;
    return { ...base, step };
  }
  if (status === DUPLICATE_STATUS && steps.includes("details")) return { ...base, step: "details" };
  return base;
}

/** A processing purchase once its status is known: settled keeps its id, failed drops it. */
function resolvedPurchase(
  purchase: WizardPurchase | null,
  outcome: "success" | "failed",
): WizardPurchase | null {
  if (purchase === null || outcome === "failed") return null;
  return { ...purchase, outcome };
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "next":
      return { ...state, step: stepBy(state, 1), notice: null };
    case "back":
      return { ...state, step: stepBy(state, -1), notice: null };
    case "change": {
      // "Change" goes back to the payment step; the send-time choice is kept (R16).
      const hasPayment = wizardSteps(state.plan).includes("payment");
      return { ...state, step: hasPayment ? "payment" : state.step, notice: null };
    }
    case "replan":
      return { ...state, plan: action.plan, step: clampStep(state.step, action.plan) };
    case "pickSendMode":
      return { ...state, sendTime: { ...state.sendTime, mode: action.mode } };
    case "setSendDate":
      return { ...state, sendTime: { ...state.sendTime, date: action.date } };
    case "setSendTime":
      return { ...state, sendTime: { ...state.sendTime, time: action.time } };
    case "setBatchSendDate":
      return { ...state, batchSendDate: action.date };
    case "purchased":
      return { ...state, purchase: action.purchase };
    case "purchaseResolved":
      return { ...state, purchase: resolvedPurchase(state.purchase, action.outcome) };
    case "pickDepositMode":
      return { ...state, depositPreference: action.mode };
    case "submit":
      return { ...state, busy: true, notice: null };
    case "failed":
      return failedState(state, action.status, action.message);
    case "dismissNotice":
      return { ...state, notice: null };
  }
}

/** The primary button: the write on the last step, else Continue (to payment, from paid details). */
export function primaryActionLabel(step: WizardStep, plan: WizardPlan, n: number): string {
  const actions = COPY.addWizard.actions;
  const steps = wizardSteps(plan);
  if (step === steps[steps.length - 1]) return n === 1 ? actions.addOne : actions.addMany(n);
  return step === "details" && plan.paid ? actions.continueToPayment : actions.continue;
}

/**
 * Whether the primary button is refused. The figures and the credits still to buy gate the payment
 * step and the write of a paid addition (B4, B5, R15); a re-quote in flight holds both, so the write
 * echoes the figures shown.
 */
export function primaryDisabled(i: {
  state: WizardState;
  quote: RecipientAdditionsQuote | undefined;
  fetching: boolean;
  sendError: string | null;
  importBlocked?: boolean;
}): boolean {
  const { state } = i;
  if (state.busy) return true;
  if (i.importBlocked) return true;
  if (state.step === "sendTime" && i.sendError !== null) return true;
  if (state.step !== "payment" && !isLastStep(state)) return false;
  if (i.fetching) return true;
  const gated = state.step === "payment" || state.plan.paid;
  return gated && !canContinuePayment(i.quote, false);
}

/** On send time, a re-quote that is short of credits again needs review on the payment step. */
export function needsPaymentReview(state: WizardState, quote: RecipientAdditionsQuote): boolean {
  if (state.step !== "sendTime" || !wizardSteps(state.plan).includes("payment")) return false;
  return Number(quote.creditsToBuy) > 0;
}

/** Why the send-time step can't confirm: the batch date (bulk automation) or the chosen time. */
export function sendStepError(
  state: WizardState,
  campaign: SendTimeCampaign,
  now: Date,
): string | null {
  if (state.step !== "sendTime") return null;
  if (campaign.automation) {
    const zone = campaignZone(campaign);
    return recipientSendDateError(state.batchSendDate, now, campaign.expireDate, zone);
  }
  return sendTimeError(campaign, state.sendTime, now);
}

/**
 * The details sub-line. A free add with a send step numbers details as "Step 1 of 2"; a paid add
 * numbers payment and send time instead, so details names the campaign and its budget.
 */
function detailsSubtitle(plan: WizardPlan, campaignName: string, giftsBudgeted: number | null) {
  const copy = COPY.addWizard;
  if (plan.draft) return copy.detailsSubDraft(campaignName);
  if (!plan.paid && plan.sendStep) return copy.detailsSubStep;
  return giftsBudgeted === null ? campaignName : copy.detailsSub(campaignName, giftsBudgeted);
}

export function wizardTitle(
  step: WizardStep,
  plan: WizardPlan,
  n: number,
  campaignName: string,
  giftsBudgeted: number | null,
): { title: string; subtitle: string } {
  const copy = COPY.addWizard;
  if (step === "payment") {
    const twoStep = wizardSteps(plan).includes("sendTime");
    return {
      title: copy.paymentTitle(n, campaignName),
      subtitle: twoStep ? copy.paymentSub.twoStep : copy.paymentSub.oneStep,
    };
  }
  if (step === "sendTime") return { title: copy.sendTitle(n), subtitle: copy.sendSub };
  return {
    title: copy.detailsTitle,
    subtitle: detailsSubtitle(plan, campaignName, giftsBudgeted),
  };
}

// ---- the recipient form (A7, R17) ---------------------------------------------------------------

/** A trimmed optional field, or undefined when blank (so it is left off the wire). */
function optionalText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function emailError(email: string, existingEmails: readonly string[]): string | null {
  const errors = COPY.recipientForm.errors;
  if (!isValidContactEmail(email)) return errors.email;
  const target = contactEmailKey(email);
  return existingEmails.some((e) => contactEmailKey(e) === target) ? errors.duplicate : null;
}

/**
 * True when `date` (YYYY-MM-DD) is before the close instant's calendar day in `timeZone` — the
 * API's rule. No close (or an unreadable one) never refuses.
 */
function isSendDateBeforeClose(date: string, expireDate: string | null, timeZone: string): boolean {
  const closeDay = zonedDateInput(expireDate, timeZone);
  return closeDay === undefined || date < closeDay;
}

/**
 * A recipient's (or a bulk batch's) send date: after today and before the campaign closes, both
 * calendar days in `timeZone` (the campaign's).
 */
export function recipientSendDateError(
  date: string,
  now: Date,
  expireDate: string | null,
  timeZone: string,
): string | null {
  const errors = COPY.recipientForm.errors;
  if (!isFutureSendDateInput(date, timeZone, now)) return errors.sendDate;
  return isSendDateBeforeClose(date, expireDate, timeZone) ? null : errors.sendDateAfterClose;
}

/**
 * Field errors for the add form. First name, a valid unique email and a country are
 * required; an automation also needs a future send date before the close, read in `timeZone` (the
 * campaign's). Last name never is.
 */
export function recipientFormErrors(
  v: RecipientFormValue,
  ctx: {
    automation: boolean;
    existingEmails: readonly string[];
    now: Date;
    expireDate: string | null;
    timeZone: string;
  },
): RecipientFormErrors {
  const errors = COPY.recipientForm.errors;
  const email = emailError(v.email, ctx.existingEmails);
  const sendDate = ctx.automation
    ? recipientSendDateError(v.sendDate, ctx.now, ctx.expireDate, ctx.timeZone)
    : null;
  return {
    ...(v.firstName.trim() ? {} : { firstName: errors.firstName }),
    ...(email === null ? {} : { email }),
    ...(v.country.trim() ? {} : { country: errors.country }),
    ...(sendDate === null ? {} : { sendDate }),
  };
}

/**
 * The field a refused confirm points at: the API's duplicate-email refusal (409) goes on the Email
 * field (F1, F7). Every other refusal, including "every address is taken", is a notice only.
 */
export function confirmFieldErrors(status: number, message: string): RecipientFormErrors {
  const duplicate = COPY.recipientForm.errors.duplicate;
  return status === DUPLICATE_STATUS && message === duplicate ? { email: duplicate } : {};
}

/** The form as a wire row; the send date only travels for an automation. */
export function toAdditionRow(
  v: RecipientFormValue,
  automation: boolean,
): RecipientAdditionRowBody {
  return {
    firstName: v.firstName.trim(),
    lastName: optionalText(v.lastName),
    email: v.email.trim(),
    country: v.country.trim(),
    senderFirstName: optionalText(v.senderFirstName),
    senderLastName: optionalText(v.senderLastName),
    senderCompany: optionalText(v.senderCompany),
    sendDate: automation ? optionalText(v.sendDate) : undefined,
  };
}

/** The first contact's sender (lowest id, as the server's `firstContactSender`), else blank. */
export function defaultSender(rows: readonly CampaignContactWire[]): {
  firstName: string;
  lastName: string;
  company: string;
} {
  const first = rows.reduce<CampaignContactWire | null>(
    (min, row) => (min === null || row.id < min.id ? row : min),
    null,
  );
  return {
    firstName: first?.senderFirstName ?? "",
    lastName: first?.senderLastName ?? "",
    company: first?.senderCompany ?? "",
  };
}

/** The confirm echo for a free-only or draft addition: nothing paid or invoiced. */
export function zeroExpected(): AdditionsExpected {
  return {
    paidCount: 0,
    depositMode: null,
    claimAdded: "0",
    invoiced: null,
  };
}

/**
 * The confirm body: the rows, one send time for all of them (only when the campaign offers one)
 * the echo of the quote shown — the zero echo on a draft, which has no budget (B14) — and the id of
 * the purchase made in the Buy Credits modal, when there was one.
 */
export function additionBody(i: {
  entry: WizardEntry;
  form: RecipientFormValue;
  rows: readonly RecipientAdditionRowBody[];
  campaign: SendTimeCampaign;
  state: WizardState;
  quote: RecipientAdditionsQuote | undefined;
}): AddCampaignRecipientsBody {
  const { campaign, state } = i;
  const bulkRows = campaign.automation
    ? applyBatchSendDate(i.rows, state.batchSendDate)
    : [...i.rows];
  const recipients = i.entry === "single" ? [toAdditionRow(i.form, campaign.automation)] : bulkRows;
  const expected = state.plan.draft ? zeroExpected() : (i.quote?.expected ?? zeroExpected());
  const purchaseId = state.purchase === null ? {} : { purchaseId: state.purchase.id };
  if (sendTimeOptions(campaign).length === 0) return { recipients, expected, ...purchaseId };
  return {
    recipients,
    sendAt: toSendAt(state.sendTime, campaignZone(campaign)),
    expected,
    ...purchaseId,
  };
}

// ---- bulk rows (A5-A7, R3-R6) ------------------------------------------------------------------

/** An imported builder row as a wire row: country name/alias → ISO code (US when unknown). */
export function bulkRecipientToRow(r: Recipient): RecipientAdditionRowBody {
  const sendDate = r.sendDate?.trim();
  return {
    firstName: r.firstName.trim(),
    lastName: optionalText(r.lastName),
    email: r.email.trim(),
    country: resolveCountryCodeOrUs(r.recipientCountry),
    senderFirstName: optionalText(r.senderFirst),
    senderLastName: optionalText(r.senderLast),
    senderCompany: optionalText(r.senderCompany),
    sendDate: sendDate && isCalendarDateInput(sendDate) ? sendDate : undefined,
  };
}

/** The batch date on every undated row; a row's own date is kept. New rows, inputs untouched. */
export function applyBatchSendDate(
  rows: readonly RecipientAdditionRowBody[],
  date: string,
): RecipientAdditionRowBody[] {
  if (!date) return [...rows];
  return rows.map((row) => (row.sendDate ? row : { ...row, sendDate: date }));
}

/**
 * Splits rows into kept and skipped: an email already on the campaign or earlier in the file
 * (trimmed, case-insensitive) is skipped, the first one wins and order is kept. A blank email is
 * never a duplicate — it is an invalid row, reported by `bulkImportReview`.
 */
export function dedupeRecipientRows<T extends { email: string }>(
  rows: readonly T[],
  existing: Iterable<string>,
): { kept: T[]; skipped: T[] } {
  const seen = new Set(Array.from(existing, contactEmailKey));
  const kept: T[] = [];
  const skipped: T[] = [];
  for (const row of rows) {
    const key = contactEmailKey(row.email);
    if (key !== "" && seen.has(key)) {
      skipped.push(row);
      continue;
    }
    if (key !== "") seen.add(key);
    kept.push(row);
  }
  return { kept, skipped };
}

/** A multi-date row's own send date: a real YYYY-MM-DD day, after today and before the close. */
function ownSendDateError(
  own: string,
  ctx: { now: Date; expireDate: string | null; timeZone: string },
): string | null {
  if (!isCalendarDateInput(own)) return COPY.bulk.sendDateFormat;
  return recipientSendDateError(own, ctx.now, ctx.expireDate, ctx.timeZone);
}

/**
 * What an import shows before the wizard opens: the rows to add (campaign emails and earlier rows
 * skipped, first one wins) and, per kept row, why it can't be added. Rows are numbered as in the
 * file. Only an automation row's own send date is checked, in `ctx.timeZone` (the campaign's); an
 * undated row waits for the batch date.
 */
export function bulkImportReview(
  imported: readonly Recipient[],
  existingEmails: readonly string[],
  ctx: { automation: boolean; now: Date; expireDate: string | null; timeZone: string },
): BulkImportReview {
  const numbered = imported.map((recipient, i) => ({
    recipient,
    email: recipient.email,
    rowNo: i + 1,
  }));
  const { kept, skipped } = dedupeRecipientRows(numbered, existingEmails);
  const rows = kept.map((k) => bulkRecipientToRow(k.recipient));
  const problems = kept.flatMap(({ recipient, rowNo }, i) => {
    const row = rows[i];
    const own = recipient.sendDate?.trim() ?? "";
    let reason: string | null = null;
    if (!recipient.firstName.trim()) reason = COPY.recipientForm.errors.firstName;
    else if (!isValidContactEmail(recipient.email)) reason = COPY.recipientForm.errors.email;
    else if (
      recipient.recipientCountry?.trim() &&
      matchCountryCode(recipient.recipientCountry) === null
    ) {
      reason = COPY.recipientForm.errors.country;
    } else if (ctx.automation && own) reason = ownSendDateError(own, ctx);
    return reason === null || row === undefined
      ? []
      : [COPY.bulk.rowError(rowNo, row.email, reason)];
  });
  return {
    rows,
    skipped: skipped.length > 0 ? COPY.bulk.skipped(skipped.length) : null,
    skippedCount: skipped.length,
    errors:
      problems.length > 0
        ? [COPY.bulk.invalidRows(problems.length), ...problems, COPY.bulk.reupload]
        : [],
  };
}

/**
 * What happens after an import is reviewed: rows to add open the wizard; none left (all skipped,
 * or an empty file) is a toast with the skipped sentence, else the re-upload hint.
 */
export function bulkImportOutcome(review: BulkImportReview): BulkImportOutcome {
  if (review.rows.length > 0) return { kind: "wizard" };
  return { kind: "toast", message: review.skipped ?? COPY.bulk.reupload };
}

/**
 * A held review re-checked against the campaign's current emails: rows another tab or admin has
 * added since are dropped and counted as skipped. The same object when nothing new is on the campaign.
 */
export function refreshBulkReview(
  review: BulkImportReview,
  existingEmails: readonly string[],
): BulkImportReview {
  const { kept } = dedupeRecipientRows(review.rows, existingEmails);
  const dropped = review.rows.length - kept.length;
  if (dropped === 0) return review;
  const skippedCount = review.skippedCount + dropped;
  return { ...review, rows: kept, skipped: COPY.bulk.skipped(skippedCount), skippedCount };
}

/** The bulk import's dialogs: `imported` opens the wizard and leaves the importer as it is. */
export function bulkImportReducer(
  state: BulkImportState,
  action: BulkImportAction,
): BulkImportState {
  switch (action.type) {
    case "openImporter":
      return { ...state, importerOpen: true };
    case "closeImporter":
      return { ...state, importerOpen: false };
    case "imported":
      return { ...state, wizard: { review: action.review, open: true } };
    case "closeWizard":
      return state.wizard === null ? state : { ...state, wizard: { ...state.wizard, open: false } };
  }
}

// ---- rows added this session (B10, R23) ---------------------------------------------------------

/** New rows first, in `newIds` order; every other row keeps its place. Unknown ids are ignored. */
export function mergeNewContacts<T extends { id: string | number }>(
  rows: readonly T[],
  newIds: readonly string[],
): T[] {
  const byId = new Map(rows.map((row) => [String(row.id), row]));
  const wanted = new Set(newIds);
  const fresh = [...wanted].flatMap((id) => {
    const row = byId.get(id);
    return row === undefined ? [] : [row];
  });
  return [...fresh, ...rows.filter((row) => !wanted.has(String(row.id)))];
}

/** Records an add: its ids go in front of the earlier ones and the list's filters reset. */
export function recordAdded(state: AddedRowsState, ids: readonly number[]): AddedRowsState {
  const added = ids.map(String);
  const addedSet = new Set(added);
  return {
    newIds: [...added, ...state.newIds.filter((id) => !addedSet.has(id))],
    resetSignal: state.resetSignal + 1,
  };
}
